import React from 'react';

/**
 * The four blocking-message regions of the wizard (SPEC-SOR-321): the per-step
 * validation banner, the fee-calc entry gate on Next, the Next-adjacent blocker
 * list, and CourtFeesTab's fee-calc refusal. Each key is also the container's
 * `id` and `data-testid` — the R7 sibling focuses it by id and the gate rules
 * read the testids, so the values are pinned, never reworded.
 */
export type BlockingSurface =
  | 'validation-banner'
  | 'fee-gate-blockers'
  | 'next-blockers'
  | 'fee-calc-blocked';

/**
 * The live-region role each surface carries FROM MOUNT (R6): `alert` (assertive)
 * for a message that blocks, `status` (polite) for `next-blockers`, which
 * duplicates the validation banner's content and must not interrupt. A region
 * announces reliably only when it is already in the document before it receives
 * content, so the role is unconditional — never attached in the render that
 * fills the container.
 */
// The role decider rides beside the container it serves: the assertive/polite
// split is a property of THIS component's contract, and a second module would
// let the two halves drift.
// eslint-disable-next-line react-refresh/only-export-components
export function liveRoleForSurface(surface: BlockingSurface): 'alert' | 'status' {
  return surface === 'next-blockers' ? 'status' : 'alert';
}

interface BlockingMessageProps {
  surface: BlockingSurface;
  // False while no requirement is unmet: the container stays mounted with its
  // role (R5) but renders neither the heading nor any list, and carries none of
  // the presentation classes so nothing paints.
  populated: boolean;
  heading?: React.ReactNode;
  children?: React.ReactNode;
  // Populated-state presentation, applied ONLY while populated — an empty
  // container must have no padding/border/margin to hand to layout.
  className?: string;
  style?: React.CSSProperties;
}

/**
 * The single container for every blocking-message surface: always in the
 * document for as long as the step that owns it is rendered, carrying its
 * live-region role from mount (R5/R6). It is never `hidden` / `display:none`
 * and never unmounted on emptiness — a live region created in the same
 * operation that fills it announces nothing. Reads no context, so it renders
 * under `renderToStaticMarkup` without providers.
 */
export const BlockingMessage: React.FC<BlockingMessageProps> = ({
  surface,
  populated,
  heading,
  children,
  className,
  style,
}) => (
  <div
    role={liveRoleForSurface(surface)}
    id={surface}
    data-testid={surface}
    // The R7 sibling focuses the container by id when a block lands; -1 keeps it
    // out of the tab order.
    tabIndex={-1}
    className={populated ? className : undefined}
    style={populated ? style : undefined}
  >
    {populated && (
      <>
        {heading}
        {children}
      </>
    )}
  </div>
);
